import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inOutscapeData } from '../data/inOutscapeData';
import { ArtistSection } from './ArtistSection';
import StaticNoiseBackground from './StaticNoiseBackground';
import { ExhibitionLayout } from './layouts/ExhibitionLayout';
import { ExhibitionNavigation } from './navigation/ExhibitionNavigation';

const InOutscape = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [activeArtist, setActiveArtist] = useState<string | null>(artistId || null);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (artistId) {
      const element = document.getElementById(artistId);
      if (element) {
        setIsScrolling(true);
        element.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
        setActiveArtist(artistId);
        setTimeout(() => setIsScrolling(false), 1000);
      }
    }
  }, []);

  const scrollToArtist = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      setIsScrolling(true);
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveArtist(id);
      navigate(`/exhibitions/in-outscape/${id}`);
      setTimeout(() => setIsScrolling(false), 1000);
    }
  }, [navigate]);

  useEffect(() => {
    let scrollTimeout: number;

    const handleScroll = () => {
      if (isScrolling) return;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const sections = inOutscapeData.map(artist => ({
          id: artist.id,
          element: document.getElementById(artist.id),
        }));

        const current = sections.find(section => {
          if (!section.element) return false;
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        });

        if (current && current.id !== activeArtist) {
          setActiveArtist(current.id);
          navigate(`/exhibitions/in-outscape/${current.id}`);
        }
      }, 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [navigate, isScrolling, activeArtist]);

  useEffect(() => {
    if (artistId && artistId !== activeArtist) {
      const element = document.getElementById(artistId);
      if (element) {
        setIsScrolling(true);
        element.scrollIntoView({ behavior: 'smooth' });
        setActiveArtist(artistId);
        setTimeout(() => setIsScrolling(false), 1000);
      }
    }
  }, [artistId, activeArtist]);

  const navigationItems = inOutscapeData.map(artist => ({
    id: artist.id,
    name: artist.name,
  }));

  return (
    <ExhibitionLayout
      title="In : Outscape"
      subtitle="A dual exhibition exploring internal landscapes and outward expressions"
      openingDate="SEPTEMBER 7, 2025"
      closingDate="NOVEMBER 23, 2025"
      background={<StaticNoiseBackground />}
      navigation={
        <ExhibitionNavigation
          items={navigationItems}
          activeId={activeArtist}
          onItemClick={scrollToArtist}
          basePath="/exhibitions/in-outscape"
        />
      }
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1rem' }}>
        {inOutscapeData.map(artist => (
          <ArtistSection key={artist.id} artist={artist} />)
        )}
      </div>
    </ExhibitionLayout>
  );
};

export default InOutscape;


